import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { useConnectionStore } from '../stores/connectionStore';

type PostgresEvent = 'INSERT' | 'UPDATE' | 'DELETE' | '*';

interface SubscriptionConfig {
  channelName: string;
  table: string;
  filter?: string;
  event: PostgresEvent;
  schema?: string;
  onPayload: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void;
}

/**
 * Create a Supabase Realtime subscription with consistent error handling,
 * status logging, and cleanup pattern.
 *
 * Returns a cleanup function that uses supabase.removeChannel() (not .unsubscribe()).
 */
export function createSubscription(config: SubscriptionConfig): {
  channel: RealtimeChannel;
  cleanup: () => void;
} {
  const { channelName, table, filter, event, schema = 'public', onPayload } = config;

  const pgConfig = filter
    ? { event, schema, table, filter } as const
    : { event, schema, table } as const;

  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes' as const,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      pgConfig as any,
      (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
        onPayload(payload);
      }
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log(`[realtime] ${channelName}: subscribed`);
        useConnectionStore.getState().setStatus('connected');
      } else if (status === 'CHANNEL_ERROR') {
        console.warn(`[realtime] ${channelName}: channel error`);
        useConnectionStore.getState().handleDisconnect();
      } else if (status === 'TIMED_OUT') {
        console.warn(`[realtime] ${channelName}: timed out`);
        useConnectionStore.getState().handleDisconnect();
      } else if (status === 'CLOSED') {
        console.log(`[realtime] ${channelName}: closed`);
      }
    });

  const cleanup = () => {
    console.log(`[realtime] ${channelName}: removing channel`);
    supabase.removeChannel(channel);
  };

  return { channel, cleanup };
}

/**
 * Create a multi-table subscription (e.g. factionStore subscribes to two tables).
 * Returns cleanup function.
 */
export function createMultiSubscription(config: {
  channelName: string;
  tables: Array<{
    table: string;
    filter?: string;
    event: PostgresEvent;
    onPayload: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void;
  }>;
  schema?: string;
}): {
  channel: RealtimeChannel;
  cleanup: () => void;
} {
  const { channelName, tables, schema = 'public' } = config;

  let ch = supabase.channel(channelName);

  for (const t of tables) {
    const pgConfig = t.filter
      ? { event: t.event, schema, table: t.table, filter: t.filter } as const
      : { event: t.event, schema, table: t.table } as const;

    ch = ch.on(
      'postgres_changes' as const,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      pgConfig as any,
      (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
        t.onPayload(payload);
      }
    );
  }

  const channel = ch.subscribe((status) => {
    if (status === 'SUBSCRIBED') {
      console.log(`[realtime] ${channelName}: subscribed`);
      useConnectionStore.getState().setStatus('connected');
    } else if (status === 'CHANNEL_ERROR') {
      console.warn(`[realtime] ${channelName}: channel error`);
      useConnectionStore.getState().handleDisconnect();
    } else if (status === 'TIMED_OUT') {
      console.warn(`[realtime] ${channelName}: timed out`);
      useConnectionStore.getState().handleDisconnect();
    }
  });

  const cleanup = () => {
    console.log(`[realtime] ${channelName}: removing channel`);
    supabase.removeChannel(channel);
  };

  return { channel, cleanup };
}
